//changed due to package upgrade
import {
  alpha,
  Box,
  Button,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Form, Formik } from 'formik';
import React, { useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import AppGridContainer from '@projectK/components/AppGridContainer';
import jwtAxios from '@projectK/services/auth/JWT';
import { useInfoViewActionsContext } from '@projectK/context';
import AppInfoView from '@projectK/components/AppInfoView';
import * as yup from 'yup';
import CompanyTheme from '@projectK/constants/CompanyTheme';
import PropTypes from 'prop-types';
import {
  useRolePermissions,
  hasPermission,
} from '@projectK/context/RolesProvider';
import IntlMessages from '@projectK/helpers/IntlMessages';

const verbiageData = JSON.parse(localStorage.getItem('verbiageSettings')) || {};

const validationSchema = yup.object({
  convenience_fee_text: yup
    .string()
    .max(
      100,
      `${
        verbiageData?.convenience_fee_alias || 'Convinience Fee'
      } Text should not exceed 100 characters.`,
    )
    .nullable(),
  percentage_per_transaction: yup
    .string()
    .matches(/^\d+(\.\d+)?%?$/, () => (
      <IntlMessages id='validation.percentageInvalid' />
    ))
    .test({
      name: 'maxPercentage',
      message: <IntlMessages id='validation.percentageTooHigh' />,
      test: (value) => {
        if (!value) return true; // Allow empty values

        // Parse the percentage value and check if it's less than or equal to 100
        const parsedValue = parseFloat(value.replace('%', ''));
        return !isNaN(parsedValue) && parsedValue <= 50;
      },
    })
    .nullable(),
});
const Fee = ({ settings, fetchSettings }) => {//NOSONAR
  const companyTheme = CompanyTheme();
  const [isEdit, setIsEdit] = useState(false);
  const infoViewActionsContext = useInfoViewActionsContext();
  const { rolePermissions } = useRolePermissions();
  return (
    <>
      <Formik
        enableReinitialize={true}
        validateOnChange={true}
        initialValues={{
          convenience_fee_text: settings?.convenience_fee_text || '',
          percentage_per_transaction:
            settings?.percentage_per_transaction || '',
          amount_per_transaction: settings?.amount_per_transaction || '',
        }}
        validationSchema={validationSchema}
        onSubmit={(values, { setSubmitting }) => {
          setSubmitting(true);
          // Prepare the data for submission
          const settingsArray = Object.keys(values).map((fieldName) => ({
            setting_name: 'convenience_fee',
            setting_slug: fieldName,
            setting_value: values[fieldName] === '' ? null : values[fieldName],
            is_active: true, // Set is_active as needed
          }));

          // Make the PUT request using jwtAxios
          jwtAxios
            .put(`/app-settings/convenience_fee/`, settingsArray)
            .then((response) => {
              setSubmitting(false);
              setIsEdit(false);
              fetchSettings();
              infoViewActionsContext.showMessage(
                'Convenience Fee  Settings updated successfully!',
              );
            })
            .catch((error) => {
              // Handle the error here
              infoViewActionsContext.fetchError(error?.message);
              console.error('Error updating social settings:', error);
              setSubmitting(false);
            });
        }}
      >
        {({ resetForm, setFieldValue, touched, errors, values }) => (
          <Form noValidate autoComplete='off'>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Box>
                <Typography
                  variant='h4'
                  sx={{
                    color: companyTheme
                      ? companyTheme.primary.main
                      : 'text.primary',
                  }}
                >
                  {`${
                    verbiageData?.convenience_fee_alias || 'Convenience Fee'
                  } Settings`}
                </Typography>
              </Box>
              <Box>
                {!isEdit &&
                  hasPermission(
                    rolePermissions,
                    'Company Settings - Convenience Fee',
                    'update',
                  ) && (
                    <EditIcon
                      sx={{ cursor: 'pointer' }}
                      onClick={() => setIsEdit(true)}
                    />
                  )}
              </Box>
            </Box>
            <Divider sx={{ my: 4 }} />
            <AppGridContainer>
              <Grid size={{ xs: 12, md: 12 }}>
                <Box
                  sx={{
                    border: '1px solid #EAECF0',
                    p: 6,
                    borderRadius: 3,
                    boxShadow:
                      '0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06)',
                  }}
                >
                  <AppGridContainer>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        name='convenience_fee_text'
                        variant='outlined'
                        value={values.convenience_fee_text}
                        onChange={(event) => {
                          setFieldValue(
                            'convenience_fee_text',
                            event.target.value,
                          );
                          touched.convenience_fee_text = true;
                        }}
                        error={
                          touched.convenience_fee_text &&
                          Boolean(errors.convenience_fee_text)
                        }
                        helperText={
                          touched.convenience_fee_text &&
                          errors.convenience_fee_text
                        }
                        sx={{
                          width: '100%',
                        }}
                        label={`${
                          verbiageData?.convenience_fee_alias ||
                          'Convenience Fee'
                        } Text`}
                        InputProps={{
                          readOnly: !isEdit,
                        }}
                        slotProps={{
                          input: { maxLength: 101 },
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        name='percentage_per_transaction'
                        variant='outlined'
                        value={values.percentage_per_transaction}
                        onChange={(event) => {
                          setFieldValue(
                            'percentage_per_transaction',
                            event.target.value,
                          );
                          touched.percentage_per_transaction = true;
                        }}
                        error={
                          touched.percentage_per_transaction &&
                          Boolean(errors.percentage_per_transaction)
                        }
                        helperText={
                          touched.percentage_per_transaction &&
                          errors.percentage_per_transaction
                        }
                        sx={{
                          width: '100%',
                        }}
                        label='Percentage per Transaction'
                        slotProps={{
                          input: {
                            readOnly: !isEdit,
                          },
                        }}
                      />
                    </Grid>
                  </AppGridContainer>
                </Box>
              </Grid>
            </AppGridContainer>
            {isEdit && (
              <Stack
                direction='row'
                justifyContent='flex-end'
                spacing={5}
                sx={{ mt: 3 }}
              >
                <Button
                  sx={{
                    borderColor: companyTheme
                      ? companyTheme.primary.main
                      : '#2196f3',
                    '&:hover': {
                      borderColor: companyTheme
                        ? alpha(companyTheme.primary.main, 0.8)
                        : alpha('#2196f3', 0.8),
                    },
                    color: companyTheme ? companyTheme.primary.main : '#2196f3',
                  }}
                  onClick={() => {
                    setIsEdit(false);
                    resetForm();
                  }}
                >
                  <IntlMessages id='common.cancel' />
                </Button>
                <Button
                  variant='contained'
                  type='submit'
                  sx={{
                    backgroundColor: companyTheme
                      ? companyTheme.primary.main
                      : '#2196f3',
                    '&:hover': {
                      backgroundColor: companyTheme
                        ? alpha(companyTheme.primary.main, 0.8) // Adjust the alpha value as needed
                        : alpha('#2196f3', 0.8),
                    },
                  }}
                >
                  <IntlMessages id='common.save' />
                </Button>
              </Stack>
            )}
          </Form>
        )}
      </Formik>
      <AppInfoView />
    </>
  );
};

export default Fee;
Fee.propTypes = {
  settings: PropTypes.object,
  fetchSettings: PropTypes.func,
};
